import httpService from './httpService';
import queryString from 'query-string';
import { AxiosResponse } from 'axios';

import { IBoardDataObject } from '../../../models/types/IBoardDataObject';
import { AnalysisContentModel } from '../../../models/AnalysisContentModel';
import { AnalysisContentWidgetModel } from '../../../models/AnalysisContentWidgetModel';

export interface IWidgetData {
  amount: number;
  childNodes: any;
  dimensionId: number;
  endDate: string;
  id: number;
  isRoot: any;
  measureId: number;
  nodeId: number;
  objectId: number;
  parentNodeId: number;
  periodId: number;
  periodName: string;
  scenarioId: number;
  startDate: string;
  type: string;
  valueAssumptionId: number;
  variantId: number;
}

export interface IWidgetStaticData {
  nodeId: number;
  objectId: number;
  templatePropertyId: number;
  dataType: string;
  value: string;
  rawValue: any;
}

export interface IWidgetCombinedData {
  measureValues: Array<IWidgetData>;
  staticValues: Array<IWidgetStaticData>;
}

export interface INote {
  contentId: number;
  date: Date;
  id: number;
  inactive: boolean;
  measures: INoteMeasure[];
  name: any;
  note: string;
  objects: INoteObject[];
  periods: any;
  symbol: string;
}

interface INoteObject {
  contentObjectId: number;
  id: number;
  name: string;
  noteId: number;
  parentContentObjectId: any;
}

interface INoteMeasure {
  id: number;
  measureId: number;
  name: string;
  noteId: number;
}

interface IHasBoards {
  hasBoards: boolean;
}

const _buildValueQueryString = (nodes, nodeGroups, sliceTypes, measures, endDate, singlePeriod, includeDescendants, currency, templateProperties = null) => {
  let d = endDate.split('T');
  let parsedQuery = {
    measureId: [],
    nodeId: [],
    nodeGroupId: [],
    endDate,
    singlePeriod,
    includeDescendants,
    currency,
    templatePropertyId: [],
  };
  if (measures) parsedQuery.measureId = measures && measures.map((x) => x.id);
  if (nodeGroups) parsedQuery.nodeGroupId = nodeGroups && nodeGroups.map((x) => x.id);
  if (nodes) parsedQuery.nodeId = nodes && nodes.map((x) => x.id);
  parsedQuery.endDate = d[0];
  parsedQuery.singlePeriod = singlePeriod;
  parsedQuery.includeDescendants = includeDescendants;
  parsedQuery.currency = currency;
  if (templateProperties) parsedQuery.templatePropertyId = templateProperties.map((x) => x.templatePropertyId);
  return queryString.stringify(parsedQuery);
};

export const getCashflowData = (dashboardId: number, nodeId, valueId, investorId, scenarioId?) => {
  let query = { nodeId: {}, valueId: {} };
  query.nodeId = nodeId;
  //query.scenarioId = scenarioId; //maybe to comment out
  query.valueId = valueId;
  let cashflowQuery = queryString.stringify(query);

  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/values/cashflows?${cashflowQuery}`);
  return httpService
    .get(endpoint)
    .then((res: any) => {
      return res.data;
    })
    .catch((err) => console.log('Error on fetching CF data ', err));
};

export const nodeValues = (dashboardId: number, nodes, nodeGroups, measures, endDate, singlePeriod, includeDescendants, currency, investorId): Promise<IWidgetData[]> => {
  let query = _buildValueQueryString(nodes, nodeGroups, null, measures, endDate, singlePeriod, includeDescendants, currency);
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/nodes/values?${query}`);

  return httpService
    .get(endpoint)
    .then((res: any) => {
      return res.data;
    })
    .catch((err) => console.log('Error on fetching data ', err));
};

export const getAllBoards = () => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/`);
  return httpService
    .get(endpoint)
    .then((res) => {
      return res.data;
    })
    .then((data: IBoardDataObject[]) => {
      return data;
    });
};

export const getBoardsForInvestor = (investorId) => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}`);
  return httpService.get(endpoint).then((res: AxiosResponse<IBoardDataObject[]>) => res.data);
  // .then((data: IBoardsData[]) => {
  //   return data;
  // });
};

export const getAnalysisContent = (dashboardId: number, investorId: number) => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}`);

  return httpService
    .get(endpoint)
    .then((res) => {
      return res.data;
    })
    .then((data: IBoardDataObject) => {
      return new AnalysisContentModel(data);
    });
};

export const getAllDashboardNotes = (dashboardId: number) => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContentNotes/${dashboardId}`);
  return httpService
    .get(endpoint)
    .then((res: any) => {
      return res;
    })
    .catch((err) => console.log('Error on fetching dashboard notes ', err));
};

export const getDashboardNote = (dashboardId: number, noteId: number) => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContentNotes/${dashboardId}/${noteId}`);
  return httpService
    .get(endpoint)
    .then((res: any) => {
      return res;
    })
    .catch((err) => console.log('Error on fetching dashboard note ', err));
};

export const fetchNotes = (dashboardId: number, periodId: number) => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContentNotes/${dashboardId}/periods/${periodId}`);
  return httpService
    .get(endpoint)
    .then((res: any) => {
      return res.data;
    })
    .catch((err) => console.log('Error on fetching notes ', err));
};

export const customise = (dashboardId: string, widgets: AnalysisContentWidgetModel[]) => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/${dashboardId}/customize`);
  let tempWidgetsPayload = [];
  widgets.forEach((widget) =>
    tempWidgetsPayload.push({
      widgetId: widget.id,
      visible: widget.visible,
    })
  );

  let data = { contentId: parseInt(dashboardId), widgets: tempWidgetsPayload };
  return httpService.put(endpoint, data).catch((err) => console.log('Error on putting widgets ', err));
};

export const nodeStaticValues = (dashboardId: number, nodes, nodeGroups, templateProperties, investorId): Promise<Array<IWidgetStaticData>> => {
  let query = _buildValueQueryString(nodes, nodeGroups, null, null, null, null, null, templateProperties);
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/nodes/values/static?${query}`);
  return httpService.getTyped<Array<IWidgetStaticData>>(endpoint);
};

export const nodeCombinedValues = (
  dashboardId: number,
  nodes,
  nodeGroups,
  measures,
  templateProperties,
  endDate,
  singlePeriod,
  includeDescendants,
  currency,
  investorId
): Promise<IWidgetCombinedData> => {
  let query = _buildValueQueryString(nodes, nodeGroups, null, measures, endDate, singlePeriod, includeDescendants, currency, templateProperties);
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/nodes/values/measuresAndStatic?${query}`);
  return httpService.getTyped<IWidgetCombinedData>(endpoint);
};

export const hasBoards = () => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/hasBoards`);
  return httpService.getTyped<IHasBoards>(endpoint);
};
